import { COUNTRIES } from '@/constants/countries';
import {
  parsePhoneNumberFromString,
  type CountryCode,
  type PhoneNumber,
} from 'libphonenumber-js/max';

const DEFAULT_COUNTRY_NAME = 'Brasil';

export interface ResolvedInternationalMobile {
  readonly e164: string;
  readonly country: string;
}

export interface InternationalMobileDraft {
  readonly country: string;
  readonly phone: string;
  readonly valid: boolean;
}

export interface InternationalMobileInput {
  country: string;
  phone?: string | null;
}

function isMobile(phoneNumber: PhoneNumber): boolean {
  const type = phoneNumber.getType();
  return type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE';
}

export function resolveInternationalMobile(
  input: InternationalMobileInput,
): ResolvedInternationalMobile | null {
  if (!input || typeof input !== 'object') return null;

  const { country, phone } = input;
  if (typeof country !== 'string' || typeof phone !== 'string') return null;

  const selectedCountry = COUNTRIES.find((item) => item.name === country);
  if (!selectedCountry || !phone) return null;

  const parsed =
    parsePhoneNumberFromString(phone) ??
    parsePhoneNumberFromString(phone, selectedCountry.code as CountryCode);

  if (
    !parsed ||
    !parsed.isValid() ||
    !isMobile(parsed) ||
    parsed.country !== selectedCountry.code
  ) {
    return null;
  }

  return Object.freeze({
    e164: parsed.number,
    country: selectedCountry.code,
  });
}

export function deriveInternationalMobileDraft(
  profilePhone?: string | null,
): InternationalMobileDraft {
  if (typeof profilePhone !== 'string' || !profilePhone) {
    return Object.freeze({
      country: DEFAULT_COUNTRY_NAME,
      phone: '',
      valid: false,
    });
  }

  const parsed =
    parsePhoneNumberFromString(profilePhone) ??
    parsePhoneNumberFromString(profilePhone, 'BR');
  const inferredCountry = parsed?.country
    ? COUNTRIES.find((country) => country.code === parsed.country)
    : undefined;

  if (parsed?.isValid() && isMobile(parsed) && inferredCountry) {
    return Object.freeze({
      country: inferredCountry.name,
      phone: parsed.formatNational(),
      valid: true,
    });
  }

  return Object.freeze({
    country: DEFAULT_COUNTRY_NAME,
    phone: profilePhone,
    valid: false,
  });
}
